import { HtmlRenderer } from '@/components/HtmlRenderer';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { CopyButton } from '@/components/CopyButton';
import { CreateSessionPayload, ContentBlock } from '@/store/api/sessionsApi';
import {
  ArrowLeft,
  Play,
  PlayCircle,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  ExternalLink,
  FileText,
  Code2,
  Layers,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  Quote,
  Terminal,
} from 'lucide-react';

const resourceIconMap: Record<string, React.ReactNode> = {
  github: <ExternalLink size={20} />,
  paper: <FileText size={20} />,
  repo: <Code2 size={20} />,
  slides: <Layers size={20} />,
  external: <ExternalLink size={20} />,
  other: <Layers size={20} />,
};

const resourceLabelMap: Record<string, string> = {
  github: 'GitHub',
  paper: 'Read',
  repo: 'Repo',
  slides: 'Slides',
  external: 'Visit',
  other: 'Explore',
};

function getInitials(name: string) {
  if (!name) return 'S';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Emulate PhotoGallery from Portal
function PhotoGallery({ images }: { images: { url: string; caption?: string }[] }) {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const validImages = images
    .filter((img) => img.url?.trim())
    .map((img) => ({
      ...img,
      url: img.url.startsWith('http')
        ? img.url
        : `${import.meta.env.VITE_API_URL || ''}${img.url.startsWith('/') ? '' : '/'}${img.url}`,
    }));
  if (validImages.length === 0) return null;

  const openLightbox = (idx: number) => setLightboxIdx(idx);
  const closeLightbox = () => setLightboxIdx(null);
  const prevImg = () =>
    setLightboxIdx((i) => (i !== null ? (i - 1 + validImages.length) % validImages.length : null));
  const nextImg = () => setLightboxIdx((i) => (i !== null ? (i + 1) % validImages.length : null));

  useEffect(() => {
    if (lightboxIdx === null) return;
    document.body.style.overflow = 'hidden';
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prevImg();
      if (e.key === 'ArrowRight') nextImg();
    };
    window.addEventListener('keydown', handler);
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handler);
    };
  }, [lightboxIdx]);

  const n = validImages.length;
  const imgCls =
    'absolute inset-0 w-full h-full object-cover cursor-pointer transition-transform duration-500 hover:scale-105';
  const wrapCls = 'relative overflow-hidden rounded-xl bg-slate-100';

  let gridContent;
  if (n === 1) {
    gridContent = (
      <img
        src={validImages[0]!.url}
        alt={validImages[0]!.caption || 'Gallery image'}
        className="w-full h-auto rounded-2xl border border-slate-200 shadow-sm cursor-pointer transition-transform duration-500 hover:shadow-md"
        onClick={() => openLightbox(0)}
      />
    );
  } else if (n === 2) {
    gridContent = (
      <div className="grid grid-cols-2 gap-3">
        {validImages.map((img, i) => (
          <div
            key={i}
            className={`${wrapCls} aspect-square md:aspect-video`}
            onClick={() => openLightbox(i)}
          >
            <img src={img.url} className={imgCls} />
          </div>
        ))}
      </div>
    );
  } else if (n === 3) {
    gridContent = (
      <div className="grid grid-cols-2 gap-3">
        <div className={`${wrapCls} aspect-square`} onClick={() => openLightbox(0)}>
          <img
            src={validImages[0]!.url}
            alt={validImages[0]!.caption || 'Gallery image 1'}
            className={imgCls}
          />
        </div>
        <div className="grid grid-rows-2 gap-3 h-full">
          <div className={`${wrapCls} h-full`} onClick={() => openLightbox(1)}>
            <img
              src={validImages[1]!.url}
              alt={validImages[1]!.caption || 'Gallery image 2'}
              className={imgCls}
            />
          </div>
          <div className={`${wrapCls} h-full`} onClick={() => openLightbox(2)}>
            <img
              src={validImages[2]!.url}
              alt={validImages[2]!.caption || 'Gallery image 3'}
              className={imgCls}
            />
          </div>
        </div>
      </div>
    );
  } else {
    const displayImages = validImages.slice(0, 4);
    const remainingCount = n - 4;
    gridContent = (
      <div className="grid grid-cols-2 gap-3">
        {displayImages.map((img, i) => (
          <div
            key={i}
            className={`${wrapCls} aspect-square md:aspect-video relative`}
            onClick={() => openLightbox(i)}
          >
            <img src={img.url} alt={img.caption || `Gallery image ${i + 1}`} className={imgCls} />
            {i === 3 && remainingCount > 0 && (
              <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-[2px] flex items-center justify-center text-white text-3xl font-light hover:bg-slate-900/50 transition-colors cursor-pointer">
                +{remainingCount}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <section id="gallery" className="mb-6 scroll-mt-32">
      <div className="mb-8">
        <span className="font-mono text-[11px] tracking-widest uppercase text-blue-600 font-bold block mb-2">
          // From the Community
        </span>
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <h3 className="text-2xl font-bold text-slate-900 m-0">Moments from the Centre</h3>
          <p className="text-slate-500 text-[14px] leading-relaxed max-w-xs md:text-right">
            From cryptography workshops to research discussions — explore the people, ideas and
            moments shaping our community.
          </p>
        </div>
      </div>
      <div className="relative">{gridContent}</div>
      {lightboxIdx !== null && (
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-4 right-4 z-10 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full w-10 h-10 flex items-center justify-center transition-all"
            onClick={closeLightbox}
            aria-label="Close"
          >
            <X size={20} />
          </button>

          {validImages.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full w-12 h-12 flex items-center justify-center transition-all"
                onClick={(e) => {
                  e.stopPropagation();
                  prevImg();
                }}
                aria-label="Previous"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full w-12 h-12 flex items-center justify-center transition-all"
                onClick={(e) => {
                  e.stopPropagation();
                  nextImg();
                }}
                aria-label="Next"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={validImages[lightboxIdx]!.url}
              className="max-h-[80vh] w-full object-contain rounded-xl shadow-2xl"
            />
          </div>
        </div>
      )}
    </section>
  );
}

export function AdminSessionPreviewModal({
  form,
  onClose,
}: {
  form: CreateSessionPayload;
  onClose: () => void;
}) {
  const [activeSection, setActiveSection] = useState('');
  const isScrolling = useRef(false);

  const sectionIds = useMemo(
    () => form.contentBlocks?.map((b) => b.id) || [],
    [form.contentBlocks]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries.find((entry) => entry.isIntersecting);
        if (visibleEntry && !isScrolling.current) setActiveSection(visibleEntry.target.id);
      },
      {
        root: document.getElementById('preview-modal-scroll-container'),
        rootMargin: '-120px 0px -40% 0px',
        threshold: 0,
      }
    );
    sectionIds.forEach((id: string) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, [form.contentBlocks]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // We duplicate some of the Portal's global styles via inline styles or tailwind classes to ensure it doesn't leak or looks identical.

  return (
    <div
      id="preview-modal-scroll-container"
      className="fixed inset-0 z-[100] bg-white overflow-y-auto w-full h-full !m-0 flex flex-col font-sans"
    >
      {/* Modal Close Header */}
      <div className="sticky top-0 z-[90] bg-white/80 backdrop-blur border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
          <span className="text-sm font-bold text-slate-800">Live Portal Preview</span>
        </div>
        <button
          onClick={onClose}
          className="bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-full p-2 transition-colors flex items-center gap-2 px-4"
        >
          <span className="text-sm font-bold">Close Preview</span>
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 bg-white pt-4 lg:pt-6 pb-8 lg:pb-12 text-slate-900">
        <div className="max-w-[1600px] mx-auto px-5 lg:px-10">
          <div className="mb-6 lg:mb-10">
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft size={16} /> Back to All Sessions
            </button>
          </div>

          <div className="grid lg:grid-cols-2 gap-4 lg:gap-6 mb-4 lg:mb-6">
            <div className="flex flex-col justify-center order-2 lg:order-1 min-w-0">
              <span className="font-mono text-[11px] tracking-widest uppercase text-blue-600 mb-3 lg:mb-4 font-bold">
                // {form.tag || 'TAG'} SESSION
              </span>

              <h1 className="text-[28px] md:text-4xl lg:text-5xl xl:text-[54px] font-bold text-slate-900 leading-[1.15] lg:leading-[1.1] mb-3 tracking-tight">
                {form.title || 'Untitled Session'}
              </h1>

              <div className="text-slate-600 text-[15px] lg:text-[17px] leading-relaxed mb-4 max-w-xl [&>p]:mb-4 last:[&>p]:mb-0">
                {form.description ? <HtmlRenderer html={form.description} /> : 'Session description will appear here.'}
              </div>

              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500 font-medium mb-4 lg:mb-6">
                <span>{form.duration || '0m'}</span>
                <span>·</span>
                {form.date && (
                  <>
                    <span>{form.date}</span>
                    <span>·</span>
                  </>
                )}
                <span>Recorded Session</span>
                <span>·</span>
                <span className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase ml-1">
                  {form.tag || 'TAG'}
                </span>
              </div>

              <div className="w-full h-px bg-slate-100 mb-4 max-w-xl" />

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-slate-950 overflow-hidden shrink-0 flex items-center justify-center text-white font-bold">
                  {form.speakerImageUrl ? (
                    <img
                      src={
                        form.speakerImageUrl.startsWith('http')
                          ? form.speakerImageUrl
                          : `${import.meta.env.VITE_API_URL || ''}${form.speakerImageUrl.startsWith('/') ? '' : '/'}${form.speakerImageUrl}`
                      }
                      alt={form.speaker || 'Speaker'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    getInitials(form.speaker)
                  )}
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-sm lg:text-[15px] mb-0.5">
                    {form.speaker || 'Speaker Name'}
                  </p>
                  <p className="text-xs lg:text-[13px] text-slate-500">
                    {form.speakerRole || 'Speaker Role'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-center order-1 lg:order-2 min-w-0">
              <div className="relative aspect-[16/10] lg:aspect-[16/9] bg-slate-100 rounded-2xl overflow-hidden mb-4 border border-slate-200">
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{
                    backgroundImage: form.thumbnailUrl
                      ? `url('${form.thumbnailUrl}')`
                      : `url('https://images.unsplash.com/photo-1639322537228-f710d846310a?q=80&w=1000&auto=format&fit=crop')`,
                  }}
                />
              </div>

              {/* YouTube button temporarily hidden per request */}
              {/*
              {form.youtubeUrl ? (
                <a
                  href={form.youtubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3.5 lg:py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold flex items-center justify-center gap-2 lg:gap-3 transition-colors text-sm lg:text-[15px]"
                >
                  <PlayCircle size={20} /> Watch Session on YouTube{' '}
                  <ArrowUpRight size={18} className="opacity-70" />
                </a>
              ) : (
                <button
                  disabled
                  className="w-full py-3.5 lg:py-4 bg-slate-200 text-slate-400 rounded-xl font-bold flex items-center justify-center gap-2 lg:gap-3 text-sm lg:text-[15px] cursor-not-allowed"
                >
                  <PlayCircle size={20} /> Recording Coming Soon
                </button>
              )}
              */}
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-4 lg:gap-6 relative lg:border-t lg:border-slate-100 pt-4 lg:pt-6">
            <div className="lg:col-span-8 min-w-0">
              <div className="prose prose-slate prose-lg max-w-none prose-headings:text-slate-900 prose-a:text-blue-600 prose-h3:font-bold prose-h4:font-bold prose-p:text-slate-600">
                {form.contentBlocks?.map((block) => {
                  if (
                    block.type === 'markdown' ||
                    block.type === 'overview' ||
                    block.type === 'deep-dive' ||
                    block.type === 'custom'
                  ) {
                    if (!block.data.text) return null;
                    return (
                      <section key={block.id} id={block.id} className="mb-6 scroll-mt-32 flow-root">
                        {block.title && <h3 className="text-2xl mb-3">{block.title}</h3>}
                        <HtmlRenderer html={block.data.text} />
                      </section>
                    );
                  }
                  if (block.type === 'learning_points' || block.type === 'learning-points') {
                    const points: string[] = (block.data.points || []).filter(
                      (p: any) => typeof p === 'string' && p.trim() !== ''
                    );
                    if (points.length === 0) return null;
                    return (
                      <section key={block.id} id={block.id} className="mb-6 scroll-mt-32">
                        {block.title && <h3 className="text-2xl mb-3">{block.title}</h3>}
                        <div className="grid md:grid-cols-2 gap-4 not-prose">
                          {points.map((text, i) => (
                            <div
                              key={i}
                              className="flex gap-3 bg-slate-50 p-5 rounded-xl border border-slate-100 items-start"
                            >
                              <CheckCircle2 size={20} className="text-blue-600 shrink-0 mt-0.5" />
                              <span className="text-slate-700 font-medium text-sm leading-relaxed">
                                {text}
                              </span>
                            </div>
                          ))}
                        </div>
                      </section>
                    );
                  }
                  if (block.type === 'gallery') {
                    const images = block.data.images || [];
                    if (images.length === 0) return null;
                    return (
                      <section
                        key={block.id}
                        id={block.id}
                        className="mb-6 scroll-mt-32 border-t border-slate-100 pt-8"
                      >
                        {block.title && <h3 className="text-2xl mb-3">{block.title}</h3>}
                        <PhotoGallery images={images} />
                      </section>
                    );
                  }
                  if (block.type === 'resources') {
                    const items: any[] = block.data.items || [];
                    if (items.length === 0) return null;
                    return (
                      <section
                        key={block.id}
                        id={block.id}
                        className="mb-6 scroll-mt-32 pt-6 border-t border-slate-200"
                      >
                        {block.title && <h3 className="text-2xl mb-3">{block.title}</h3>}
                        <div className="grid md:grid-cols-2 gap-5 not-prose">
                          {items.map((res, i) => (
                            <a
                              key={i}
                              href={res.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group flex items-center justify-between p-4 bg-white border border-slate-200 hover:border-blue-300 hover:shadow-[0_8px_30px_rgb(37,99,235,0.12)] hover:-translate-y-1 rounded-xl transition-all duration-300"
                            >
                              <div className="flex items-center gap-4">
                                <div className="p-2.5 bg-slate-50 text-slate-600 rounded-lg group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                                  {resourceIconMap[res.type] ?? <ExternalLink size={20} />}
                                </div>
                                <span className="font-semibold text-slate-800 text-sm">
                                  {res.label}
                                </span>
                              </div>
                              <span className="text-[11px] font-bold text-blue-600 uppercase tracking-wider flex items-center gap-1 transition-all">
                                {resourceLabelMap[res.type] ?? 'Visit'}{' '}
                                <ArrowRight
                                  size={14}
                                  className="transition-transform duration-300 group-hover:translate-x-1"
                                />
                              </span>
                            </a>
                          ))}
                        </div>
                      </section>
                    );
                  }
                  if (block.type === 'faq') {
                    const questions = block.data.questions || [];
                    if (questions.length === 0) return null;
                    return (
                      <section key={block.id} id={block.id} className="mb-8 scroll-mt-32">
                        {block.title && <h3 className="text-2xl mb-3">{block.title}</h3>}
                        <div className="divide-y divide-slate-200 border-y border-slate-200 not-prose">
                          {questions.map((q: any, i: number) => (
                            <details key={i} className="group py-4">
                              <summary className="flex items-center justify-between font-semibold text-slate-800 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                                <span className="text-[15px] pr-4">{q.q}</span>
                                <ChevronDown
                                  size={18}
                                  className="text-slate-400 group-open:text-blue-600 group-open:rotate-180 transition-transform duration-300 shrink-0"
                                />
                              </summary>
                              <div className="text-slate-600 text-[15px] leading-relaxed pt-3">
                                <HtmlRenderer html={q.a} />
                              </div>
                            </details>
                          ))}
                        </div>
                      </section>
                    );
                  }

                  if (block.type === 'callout') {
                    if (!block.data.text) return null;
                    const style = block.data.style || 'info';
                    const isQuote = style === 'quote';
                    const bgMap: Record<string, string> = {
                      info: 'bg-blue-50/80 border-blue-200',
                      warning: 'bg-amber-50/80 border-amber-200',
                      success: 'bg-green-50/80 border-green-200',
                      quote: 'bg-slate-50 border-slate-200 border-l-[6px] border-l-slate-400',
                    };
                    const textMap: Record<string, string> = {
                      info: 'text-blue-900',
                      warning: 'text-amber-900',
                      success: 'text-green-900',
                      quote: 'text-slate-700 italic',
                    };
                    const iconMap: Record<string, any> = {
                      info: <AlertCircle size={22} className="text-blue-600" />,
                      warning: <AlertCircle size={22} className="text-amber-600" />,
                      success: <CheckCircle2 size={22} className="text-green-600" />,
                      quote: <Quote size={22} className="text-slate-400" />,
                    };

                    return (
                      <section key={block.id} id={block.id} className="mb-8 scroll-mt-32">
                        {block.title && <h3 className="text-2xl mb-3">{block.title}</h3>}
                        <div
                          className={`flex gap-4 md:gap-5 p-5 md:p-6 rounded-2xl border shadow-sm not-prose ${bgMap[style]}`}
                        >
                          <div className="shrink-0 bg-white p-2.5 rounded-full shadow-sm border border-slate-200/60 h-max flex items-center justify-center">
                            {iconMap[style]}
                          </div>
                          <div className="flex-1 pt-1">
                            <div
                              className={`text-[15px] md:text-base leading-relaxed font-medium ${textMap[style]} [&>p]:m-0`}
                            >
                              <HtmlRenderer html={block.data.text} />
                            </div>
                          </div>
                        </div>
                      </section>
                    );
                  }

                  if (block.type === 'code') {
                    if (!block.data.code) return null;
                    return (
                      <section key={block.id} id={block.id} className="mb-10 scroll-mt-32">
                        {block.title && <h3 className="text-2xl mb-3">{block.title}</h3>}
                        <div className="rounded-2xl overflow-hidden border border-slate-800 bg-[#0d1117] not-prose shadow-xl relative group">
                          <div className="bg-[#161b22] border-b border-slate-800 px-4 py-2 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="flex gap-1.5">
                                <div className="w-3 h-3 rounded-full bg-slate-700" />
                                <div className="w-3 h-3 rounded-full bg-slate-700" />
                                <div className="w-3 h-3 rounded-full bg-slate-700" />
                              </div>
                              <span className="text-[11px] font-mono text-slate-400 font-medium px-2 py-1 rounded-md bg-slate-800/50 uppercase tracking-wider">
                                {block.data.language || 'plaintext'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CopyButton text={block.data.code} />
                              <Terminal
                                size={14}
                                className="text-slate-500 hidden md:block ml-2 mr-1"
                              />
                            </div>
                          </div>
                          <div className="p-5 md:p-6 overflow-x-auto text-sm leading-relaxed">
                            <pre className="font-mono text-slate-300 selection:bg-blue-500/30">
                              <code>{block.data.code}</code>
                            </pre>
                          </div>
                        </div>
                      </section>
                    );
                  }

                  if (block.type === 'timeline') {
                    const steps = block.data.steps || [];
                    if (steps.length === 0) return null;
                    return (
                      <section key={block.id} id={block.id} className="mb-10 scroll-mt-32">
                        {block.title && <h3 className="text-2xl mb-3">{block.title}</h3>}
                        <div className="relative border-l border-slate-200 ml-3 space-y-8 pb-4 not-prose">
                          {steps.map((step: any, i: number) => (
                            <div key={i} className="relative pl-8">
                              <div className="absolute -left-[15px] top-0 w-7 h-7 rounded-full bg-slate-100 border-[3px] border-white flex items-center justify-center text-[11px] font-bold text-slate-600">
                                {i + 1}
                              </div>
                              <h4 className="text-base font-bold text-slate-900 mt-0.5">
                                {step.title}
                              </h4>
                              {step.description && (
                                <div className="text-slate-600 text-[14px] leading-relaxed mt-1 whitespace-pre-wrap">
                                  <HtmlRenderer html={step.description} />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </section>
                    );
                  }
                  /* Continue learning */
                  return null;
                })}
              </div>
            </div>

            <div className="hidden lg:block lg:col-span-4">
              <div className="sticky top-32">
                <nav className="flex flex-col space-y-1 bg-white shadow-[0_2px_20px_rgba(0,0,0,0.04)] p-4 lg:p-5 rounded-2xl border border-slate-100">
                  <div className="px-4 font-bold text-slate-400 text-[11px] uppercase tracking-widest mb-3 mt-1">
                    In This Session
                  </div>
                  {form.contentBlocks?.map((block) => {
                    if (!block.title) return null;

                    // Filter out empty blocks so they don't show in the sidebar
                    if (
                      ['markdown', 'overview', 'deep-dive', 'custom', 'callout'].includes(
                        block.type
                      ) &&
                      !block.data?.text
                    )
                      return null;
                    if (['learning_points', 'learning-points'].includes(block.type)) {
                      const points = (block.data?.points || []).filter(
                        (p: any) => typeof p === 'string' && p.trim() !== ''
                      );
                      if (points.length === 0) return null;
                    }
                    if (
                      block.type === 'gallery' &&
                      (!block.data?.images || block.data.images.length === 0)
                    )
                      return null;
                    if (
                      block.type === 'resources' &&
                      (!block.data?.items || block.data.items.length === 0)
                    )
                      return null;
                    if (
                      block.type === 'faq' &&
                      (!block.data?.questions || block.data.questions.length === 0)
                    )
                      return null;
                    if (block.type === 'code' && !block.data?.code) return null;
                    if (
                      block.type === 'timeline' &&
                      (!block.data?.steps || block.data.steps.length === 0)
                    )
                      return null;

                    const isActive = activeSection === block.id;

                    return (
                      <a
                        key={block.id}
                        href={`#${block.id}`}
                        className={`group flex items-center gap-3 px-4 py-2.5 text-[13px] rounded-xl transition-all duration-200 ${
                          isActive
                            ? 'text-blue-600 bg-blue-50/50 font-semibold'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50 font-medium'
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveSection(block.id);
                          isScrolling.current = true;
                          const el = document.getElementById(block.id);
                          if (el) {
                            const container = document.getElementById(
                              'preview-modal-scroll-container'
                            );
                            if (container) {
                              const y =
                                el.getBoundingClientRect().top +
                                container.scrollTop -
                                container.getBoundingClientRect().top -
                                100;
                              container.scrollTo({ top: y, behavior: 'smooth' });
                            }
                          }
                          setTimeout(() => {
                            isScrolling.current = false;
                          }, 2000);
                        }}
                      >
                        <div
                          className={`w-1.5 h-1.5 rounded-full shrink-0 transition-colors duration-200 ${
                            isActive
                              ? 'bg-blue-600 shadow-[0_0_8px_rgba(37,99,235,0.4)]'
                              : 'bg-slate-200 group-hover:bg-slate-400'
                          }`}
                        />
                        <span className="truncate">{block.title}</span>
                      </a>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
